import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { AttachmentDto } from '../../models/attachment/attachment-dto.model';
import { OfflineUploadResult, UploadResult } from './attachment-transfer.service';
import { CreateMetadata } from './attachment.service';

/** Id prefix of attachments created on-device that have not been uploaded yet.
 * For such ids there is no server copy — a missing local blob is unrecoverable. */
export const ATTACHMENT_LOCAL_PREFIX = 'local|';
export const ATTACHMENT_PAGINATION_DEFAULT = 5;

export const AttachmentServiceToken = new InjectionToken('AttachmentServiceToken');
/**
 * Platform-specific access to attachment binaries (and, on demand, their metadata).
 * Provided per platform in `app.module.ts`:
 * - Native: `AttachmentService` — metadata in the local `attachment.db` (filled by sync),
 *   binaries in a local blob store with on-demand download + write-through.
 * - Web: `WebAttachmentService` — metadata always live from the server, binaries fetched
 *   on demand into an evictable IndexedDB LRU cache; nothing is permanently local.
 *
 * Note: attachment *metadata* and *binary* are stored and resolved independently —
 * either one can be unavailable while the other exists. See the implementations for the
 * concrete behavior per online/offline constellation.
 */
export interface IAttachmentService {
  init(): Promise<void>;
  /** Persist metadata locally. Effectively a no-op in both current implementations. */
  save(attachments: AttachmentDto[]): Promise<void>;
  /** Resolve the binary of an attachment in the requested representation.
   * Local/cache-first where a local store exists; rejects when the binary is unresolvable
   * (see implementations for the platform-specific failure modes). */
  get<T extends 'blobUrl' | 'blob' | 'base64'>(id: string, returnValue: T): Promise<T extends 'blob' ? Blob : string>;
  create(base64String: string, metadata: CreateMetadata): Promise<UploadResult>;
  update(attachment: AttachmentDto): Promise<boolean>;
  delete(id: string): Promise<void>;
  runAttachmentQueuesProcessor(): Promise<void>;
  /** Fetch the binary from the server (native: also persists it locally; web: caller caches). */
  download(id: string, mimeType: string): Promise<Blob>;
  upload(localId: string): Promise<UploadResult | null>;
  get offlineTxSuccess(): Observable<OfflineUploadResult>;
  getLocalAttachments(): Promise<string[]>;
  softDelete(attachment: AttachmentDto): Promise<boolean>;
  /** Resolve the metadata DTO (native: local db only; web: live from the server). */
  getAttachmentDto(id: string): Promise<AttachmentDto>;
  isAttachmentBlobLocallyAvailable(id: string): Promise<boolean>;
}
